import {
  AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, NgZone, OnDestroy, Output, ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import * as L from 'leaflet';
import area from '@turf/area';
import { polygon as turfPolygon } from '@turf/helpers';
import { Subscription, forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Widget, WidgetSensorCategory } from '@models/widget/widget.model';
import {
  MAP_DEVICE_COLORS, MAP_SENSOR_COLORS, MapDevice, MapDeviceStatus, MapPolygon, MapSensor, MapSensorStatus,
  MapVariable, MapVariableValue, WidgetMapData, mapDeviceName,
} from '@models/dashboard/dashboard-map.model';
import { DashboardService } from '@shared/services/dashboard.service';
import { SensorCatalogService } from '@shared/services/sensor-catalog.service';
import { StorageService } from '@services/storage.service';
import { ToastService } from '@shared/services/toast.service';
import { ArcgisMapService } from '@shared/services/arcgis-map.service';

/** Fila de la cajita o de la ficha: un sensor con su valor principal ya formateado. */
interface MapSensorRow {
  sensor:    MapSensor;
  typeLabel: string;
  mainValue: string;
  ago:       string;
}

/** Lo que necesita la ficha (sin coordenadas: vale tambien para los que no tienen ubicacion). */
type DetailDevice = Omit<MapDevice, 'lat' | 'long'>;

/** Cajita movida: desplazamiento en px respecto a su sitio y su tamaño (para saber donde acaba la linea). */
interface LabelOffset { x: number; y: number; width: number; height: number }

/** Arrastre de una cajita por su cabecera: desde donde empezo y si ya se ha movido (si no, era un clic). */
interface LabelDrag { id: string; pointerId: number; startX: number; startY: number; from: LabelOffset; moved: boolean }

/** Cajita al separarlas, en px del mapa. `fixed`: no se mueve (movida a mano, chincheta o panel): las demas se apartan. */
interface LabelBox {
  id: string; left: number; top: number; width: number; height: number;
  homeLeft: number; homeTop: number; fixed: boolean;
}

const MAX_LABEL_ROWS = 4;           // con mas sensores: 3 filas + "+N mas"
const LABELS_MIN_ZOOM = 14;          // por debajo, las cajitas se esconden solas
const SMALL_WIDGET_WIDTH = 420;      // widget estrecho (o movil): cajitas ocultas al empezar
const LABEL_ABOVE_PIN = 18;          // la cajita va centrada esta distancia por encima de la chincheta
const LABEL_PIN_RADIUS = 10;         // zona de la chincheta que una cajita apartada no debe tapar
const LABEL_GAP = 6;                 // espacio minimo entre dos cajitas al separarlas
const LABEL_SEARCH_STEP = 24;        // al buscar hueco: cada cuantos px se amplia el circulo
const LABEL_SEARCH_ANGLES = 16;      // y cuantas posiciones se prueban en cada circulo
const MAP_PANELS = '.widget-map__layers, .widget-map__notice, .widget-map__tray, .leaflet-control';
// Color de una variable si su entrada aun no tiene (normalmente lo trae la entrada, como en el grafico)
const VARIABLE_FALLBACK_COLORS = ['#1c6fd8', '#8e44ad', '#e67e22', '#16a085', '#c0392b', '#2c3e50'];
/** Polígono sin color propio (formato anterior, sin regla): el azul del contorno de siempre. */
const DEFAULT_POLYGON_COLOR = '#1c6fd8';
/** Leyenda: un grupo con hasta tantas áreas sale abierto; con más, plegado (se abre al pulsarlo). */
const GROUP_AREAS_OPEN_MAX = 6;

interface PolygonLegendArea { key: string; name: string; color: string }
/** Leyenda de una regla de polígono: el nombre de la propiedad y sus áreas, cada una con su color. */
interface PolygonLegendGroup { id: string; label: string; areas: PolygonLegendArea[] }

@Component({
  standalone: false,
  selector: 'app-widget-map',
  templateUrl: './widget-map.component.html',
  styleUrls: ['./widget-map.component.scss'],
})
export class WidgetMapComponent implements AfterViewInit, OnDestroy {
  @Input({ required: true }) widget!: Widget;
  @Input() showChrome = false;
  @Input() isEditable = false;
  @Input() canDelete = true;
  @Input() isEditMode = false;
  @Input() isMockPreview = false;
  @Input() isPreview = false;
  @Input() sensorTypeLabels: string[] = [];

  @Output() edit   = new EventEmitter<void>();
  @Output() remove = new EventEmitter<void>();

  @ViewChild('mapCanvas') mapCanvas?: ElementRef<HTMLDivElement>;

  loading = true;
  loadError = false;
  data: WidgetMapData | null = null;
  labelsOn = true;
  /** Dispositivo de la ficha: puede ser uno sin ubicacion (no tiene lat/long). */
  selected: DetailDevice | null = null;
  outsideDevices: MapDevice[] = [];

  // Leyenda: lo que el usuario muestra u oculta. No se guarda ni cambia la configuracion del widget.
  legendOpen = true;
  polygonVisible = true;
  private hiddenVariables = new Set<string>();
  // Cajitas que el usuario cierra con su ✕ (queda la chincheta; al pulsarla se abre). No se guarda: al
  // recargar se ven todas. Y la ultima cajita pulsada se pone delante de las demas.
  private closedLabels = new Set<string>();
  private labelsTop = 0;
  // Cajitas movidas por su cabecera: desplazamiento en px respecto a su sitio (sobre la chincheta). Tampoco
  // se guarda. Una linea une la cajita movida con su chincheta.
  private labelOffsets = new Map<string, LabelOffset>();
  /** Las movidas a mano: la separacion automatica no las recoloca. */
  private manualLabels = new Set<string>();
  private labelDrag: LabelDrag | null = null;
  /** Justo despues de arrastrar: el clic que suelta la cajita no abre la ficha. */
  private ignoreNextLabelClick = false;

  private map?: L.Map;
  private polygonLayers: L.Polygon[] = [];
  /** Cada capa de polígono con su polígono (para ocultar áreas sueltas desde la leyenda). */
  private polygonLayerRecords: Array<{ polygon: MapPolygon; layer: L.Polygon }> = [];
  /** Áreas de una regla de polígono ocultas desde la leyenda (no se guarda). */
  private hiddenAreas = new Set<string>();
  private openGroups = new Set<string>();
  private closedGroups = new Set<string>();
  private polygonGroupsCache: PolygonLegendGroup[] = [];
  private polygonGroupsData: unknown = null;
  private labelsButton?: HTMLAnchorElement;
  private markers = new Map<string, { marker: L.Marker; device: MapDevice }>();
  private catalog = new Map<number, WidgetSensorCategory>();
  private subscription?: Subscription;
  private areaDashboardSubscription?: Subscription;
  private openingAreaDashboard = false;
  private resizeObserver?: ResizeObserver;

  constructor(
    private readonly dashboardService: DashboardService,
    private readonly sensorCatalog: SensorCatalogService,
    private readonly arcgis: ArcgisMapService,
    private readonly zone: NgZone,
    private readonly host: ElementRef<HTMLElement>,
    private readonly router: Router,
    private readonly storage: StorageService,
    private readonly toast: ToastService,
  ) {}

  /** El detalle del dispositivo es una pantalla de la intranet (en la plantilla del admin no hay dispositivos). */
  get canOpenDevice(): boolean {
    return this.router.url.startsWith('/intranet');
  }

  /** Abre el detalle del dispositivo (para ver sus datos o ponerle coordenadas) y vuelve aqui al salir. */
  openDevice(device: Pick<MapDevice, 'userDeviceId'>): void {
    this.router.navigate(['/intranet/devices', device.userDeviceId], { queryParams: { returnUrl: this.router.url } });
  }

  ngAfterViewInit(): void {
    if (this.isPreview || !this.widget?.id || !this.widget.dashboardId) {
      this.loading = false;
      return;
    }
    this.subscription = forkJoin({
      data: this.dashboardService.getWidgetMapData(this.widget.dashboardId, this.widget.id),
      catalog: this.sensorCatalog.getCatalog().pipe(catchError(() => of([] as WidgetSensorCategory[]))),
    }).subscribe({
      next: ({ data, catalog }) => {
        this.catalog = new Map(catalog.map(category => [category.id, category]));
        this.data = data;
        this.loading = false;
        this.labelsOn = this.host.nativeElement.clientWidth >= SMALL_WIDGET_WIDTH;
        // El contenedor del mapa existe cuando Angular pinta el estado "cargado".
        setTimeout(() => this.renderMap());
      },
      error: () => {
        this.loading = false;
        this.loadError = true;
      },
    });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    this.areaDashboardSubscription?.unsubscribe();
    this.resizeObserver?.disconnect();
    window.removeEventListener('pointermove', this.onLabelPointerMove);
    window.removeEventListener('pointerup', this.onLabelPointerUp);
    window.removeEventListener('pointercancel', this.onLabelPointerUp);
    this.map?.remove();
    this.map = undefined;
    this.polygonLayerRecords = [];
  }

  // ─── Plantilla ─────────────────────────────────────────────────────────────

  /** Del area (o manuales) sin coordenadas: salen en el panel de abajo, no en el mapa. */
  get withoutLocation(): DetailDevice[] {
    return this.data?.devicesWithoutLocation ?? [];
  }

  trackById = (_: number, device: { id: string }) => device.id;

  get hasContent(): boolean {
    // Con solo dispositivos sin ubicacion tambien se muestra el mapa: salen en el panel de abajo. Y con solo
    // los contornos de las subareas (el area sin su poligono): se ven, con el aviso de que falta el suyo.
    return !!this.data
      && (this.hasPolygons || this.data.devices.length > 0 || this.data.devicesWithoutLocation.length > 0);
  }

  /**
   * Aviso de que falta el contorno (el mensaje central y el aviso del mapa dicen lo mismo): o el area no tiene el
   * poligono dibujado (sin pedir que lo dibuje: quien lo ve puede no tener permiso) o el contorno del widget esta
   * mal configurado (lo corrige el admin).
   */
  get polygonNotice(): string | null {
    if (this.data?.polygonMisconfigured) {
      return 'El contorno de este mapa no está bien configurado. Avisa al administrador.';
    }
    if (this.data?.polygonMissing) {
      return 'Esta área no tiene el polígono dibujado.';
    }
    return null;
  }
  /** El usuario cierra el aviso con su ✕ (no se guarda: al recargar vuelve a salir). */
  polygonNoticeClosed = false;

  /**
   * Leyenda del contorno: como la variable en los dispositivos, el nombre de la propiedad Polígono (el del área o,
   * si no lo tiene dibujado, el de sus subáreas). Sin nombre, el texto de siempre.
   */
  get polygonLegendLabel(): string {
    const data = this.data;
    const first = data?.polygon ?? data?.subareaPolygons[0] ?? data?.manualPolygons?.[0];
    if (first?.propertyLabel) return first.propertyLabel;
    if (data?.polygon) return 'Límite del sector';
    return data?.subareaPolygons.length ? 'Límite de las subáreas' : 'Límite de las áreas';
  }

  toggleLabels(): void {
    this.labelsOn = !this.labelsOn;
    // Mostrar con el ojo vuelve a abrir las cajitas cerradas con su ✕ y devuelve a su sitio las movidas
    if (this.labelsOn && (this.closedLabels.size || this.labelOffsets.size)) {
      this.closedLabels.clear();
      this.labelOffsets.clear();
      this.manualLabels.clear();
      this.refreshMarkers();
    }
    this.applyLabelVisibility();
    this.updateLabelsControl();
  }

  /**
   * Ojo para mostrar u ocultar las cajitas de valores. Es un ajuste de la vista, no una entrada:
   * va con los controles del mapa (encima del zoom), no en la leyenda "Entradas".
   */
  private addLabelsControl(map: L.Map): void {
    const control = new L.Control({ position: 'bottomright' });
    control.onAdd = () => {
      const bar = L.DomUtil.create('div', 'leaflet-bar wm-labels-control');
      const button = L.DomUtil.create('a', 'wm-labels-control__button', bar) as HTMLAnchorElement;
      button.href = '#';
      button.setAttribute('role', 'button');
      L.DomUtil.create('span', 'material-icons', button);
      L.DomEvent.disableClickPropagation(bar);
      L.DomEvent.on(button, 'click', event => {
        L.DomEvent.preventDefault(event);
        this.zone.run(() => this.toggleLabels());
      });
      this.labelsButton = button;
      this.updateLabelsControl();
      return bar;
    };
    control.addTo(map);
  }

  private updateLabelsControl(): void {
    const button = this.labelsButton;
    if (!button) return;
    const text = this.labelsOn ? 'Ocultar valores' : 'Mostrar valores';
    button.title = text;
    button.setAttribute('aria-label', text);
    button.setAttribute('aria-pressed', String(this.labelsOn));
    button.firstElementChild!.textContent = this.labelsOn ? 'visibility' : 'visibility_off';
  }

  // ─── Variables elegidas y leyenda ──────────────────────────────────────────

  /** Variables elegidas por el admin. Vacio = todos los dispositivos con su variable principal. */
  get variables(): MapVariable[] {
    return this.data?.variables ?? [];
  }

  get hasVariables(): boolean {
    return this.variables.length > 0;
  }

  get hasPolygons(): boolean {
    return !!this.data?.polygon || !!this.data?.subareaPolygons.length || !!this.data?.manualPolygons?.length;
  }

  isVariableVisible(id: string): boolean {
    return !this.hiddenVariables.has(id);
  }

  toggleVariable(id: string): void {
    if (this.hiddenVariables.has(id)) this.hiddenVariables.delete(id); else this.hiddenVariables.add(id);
    this.refreshMarkers();
  }

  /** Polígonos sin regla (formato anterior, sin color propio): una sola línea en la leyenda, como antes. */
  togglePolygon(): void {
    this.polygonVisible = !this.polygonVisible;
    this.applyPolygonVisibility();
  }

  /**
   * Leyenda de las reglas de polígono: un grupo por regla (el nombre de la propiedad) con sus áreas, cada una con
   * su color. Se rehace solo cuando llegan datos nuevos.
   */
  get polygonGroups(): PolygonLegendGroup[] {
    if (this.polygonGroupsData === this.data) return this.polygonGroupsCache;
    const groups = new Map<string, PolygonLegendGroup>();
    for (const polygon of this.allPolygons()) {
      if (!polygon.color || !polygon.groupId) continue;
      const group = groups.get(polygon.groupId)
        ?? { id: polygon.groupId, label: polygon.propertyLabel || 'Polígono', areas: [] };
      if (!group.areas.some(area => area.key === this.areaKey(polygon))) {
        group.areas.push({ key: this.areaKey(polygon), name: polygon.name, color: polygon.color });
      }
      groups.set(polygon.groupId, group);
    }
    this.polygonGroupsCache = [...groups.values()];
    this.polygonGroupsData = this.data;
    return this.polygonGroupsCache;
  }

  /** Hay polígonos sin regla (sin color propio): su línea de siempre en la leyenda. */
  get hasPlainPolygons(): boolean {
    return this.allPolygons().some(polygon => !polygon.color || !polygon.groupId);
  }

  isAreaVisible(key: string): boolean {
    return !this.hiddenAreas.has(key);
  }

  isGroupVisible(group: PolygonLegendGroup): boolean {
    return group.areas.some(area => this.isAreaVisible(area.key));
  }

  isGroupOpen(group: PolygonLegendGroup): boolean {
    return this.openGroups.has(group.id) || group.areas.length <= GROUP_AREAS_OPEN_MAX && !this.closedGroups.has(group.id);
  }

  toggleGroupOpen(group: PolygonLegendGroup): void {
    if (this.isGroupOpen(group)) {
      this.openGroups.delete(group.id);
      this.closedGroups.add(group.id);
    } else {
      this.closedGroups.delete(group.id);
      this.openGroups.add(group.id);
    }
  }

  toggleArea(key: string): void {
    if (this.hiddenAreas.has(key)) this.hiddenAreas.delete(key); else this.hiddenAreas.add(key);
    this.applyPolygonVisibility();
  }

  toggleGroup(group: PolygonLegendGroup): void {
    const show = !this.isGroupVisible(group);
    group.areas.forEach(area => show ? this.hiddenAreas.delete(area.key) : this.hiddenAreas.add(area.key));
    this.applyPolygonVisibility();
  }

  trackByGroup = (_: number, group: PolygonLegendGroup) => group.id;
  trackByArea = (_: number, area: PolygonLegendArea) => area.key;

  private applyPolygonVisibility(): void {
    const map = this.map;
    if (!map) return;
    this.zone.runOutsideAngular(() => this.polygonLayerRecords.forEach(({ polygon, layer }) => {
      const visible = polygon.color && polygon.groupId ? this.isAreaVisible(this.areaKey(polygon)) : this.polygonVisible;
      if (visible) layer.addTo(map); else layer.remove();
    }));
  }

  private allPolygons(): MapPolygon[] {
    const data = this.data;
    if (!data) return [];
    return [data.polygon, ...(data.otherOwnPolygons ?? []), ...data.subareaPolygons, ...(data.manualPolygons ?? [])]
      .filter((polygon): polygon is MapPolygon => !!polygon);
  }

  private areaKey(polygon: MapPolygon): string {
    return `${polygon.groupId ?? ''}|${polygon.areaEntryId.toLowerCase()}`;
  }

  variableLabel(variable: MapVariable): string {
    return variable.label || variable.fieldName;
  }

  variableColor(variable: MapVariable): string {
    return variable.color || VARIABLE_FALLBACK_COLORS[this.variables.indexOf(variable) % VARIABLE_FALLBACK_COLORS.length];
  }

  /** Valores de las variables visibles de un dispositivo (vacio sin variables). */
  visibleValues(device: Pick<MapDevice, 'values'>): MapVariableValue[] {
    return device.values.filter(value => this.isVariableVisible(value.variableId));
  }

  /**
   * Un dispositivo con valores de variables sale si le queda alguno visible. Uno sin valores sale si
   * el mapa no filtra (sin variables del admin: todos los del area) o si es un manual antiguo (sin
   * variable): esos dos casos se pintan con la variable principal de cada sensor.
   */
  isDeviceShown(device: Pick<MapDevice, 'values' | 'isManual'>): boolean {
    if (device.values.length) return this.visibleValues(device).length > 0;
    return !this.data?.filtersByVariables || device.isManual;
  }

  /** Usa los valores de las variables (y no los sensores) para pintar ese dispositivo. */
  usesVariables(device: Pick<MapDevice, 'values'>): boolean {
    return device.values.length > 0;
  }

  /** Debajo del nombre en la leyenda: de donde viene la variable. */
  variableSource(variable: MapVariable): string {
    return variable.source === 'MANUAL' ? 'Manual' : 'Dispositivos';
  }

  get shownOutside(): MapDevice[] {
    return this.outsideDevices.filter(device => this.isDeviceShown(device));
  }

  get shownWithoutLocation(): DetailDevice[] {
    return this.withoutLocation.filter(device => this.isDeviceShown(device));
  }

  variableOf(value: MapVariableValue): MapVariable | undefined {
    return this.variables.find(variable => variable.id === value.variableId);
  }

  /** "24 %" o "Sin datos" (un dato ausente no es un cero). */
  formatVariableValue(value: MapVariableValue): string {
    if (value.value === null) return 'Sin datos';
    const variable = this.variableOf(value);
    const unit = variable?.unit
      || this.catalogUnit(variable?.sensorType ?? null, variable?.fieldName ?? '');
    const text = typeof value.value === 'number'
      ? (Math.round(value.value * 10) / 10).toString().replace('.', ',')
      : value.value;
    return unit ? `${text} ${unit}` : text;
  }

  private catalogUnit(sensorType: number | null, fieldName: string): string {
    const categories = sensorType !== null ? [this.catalog.get(Number(sensorType))] : [...this.catalog.values()];
    for (const category of categories) {
      const unit = category?.variables.find(variable => variable.id === fieldName)?.unit;
      if (unit) return unit;
    }
    return '';
  }

  closeDetail(): void {
    this.selected = null;
  }

  select(device: DetailDevice): void {
    this.selected = device;
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.selected) this.closeDetail();
  }

  displayName(device: Pick<MapDevice, 'name' | 'serialNumber'>): string {
    return mapDeviceName(device);
  }

  rows(device: DetailDevice): MapSensorRow[] {
    return device.sensors.map(sensor => ({
      sensor,
      typeLabel: this.typeLabel(sensor.type),
      mainValue: this.mainValue(sensor),
      ago: this.ago(sensor.lastDate),
    }));
  }

  /** Todas las variables de un sensor para la ficha: [nombre, valor con unidad]. */
  allValues(sensor: MapSensor): Array<[string, string]> {
    return Object.entries(sensor.values).map(([key, value]) => [key, this.formatValue(sensor.type, key, value)]);
  }

  batteryLabel(device: DetailDevice): string | null {
    return device.vBus ? `${(device.vBus / 1000).toFixed(1).replace('.', ',')} V` : null;
  }

  sensorColor(status: MapSensorStatus): string {
    return MAP_SENSOR_COLORS[status];
  }

  deviceColor(status: MapDeviceStatus): string {
    return MAP_DEVICE_COLORS[status];
  }

  deviceStatusLabel(status: MapDeviceStatus): string {
    return status === 'ok' ? 'Envía' : status === 'partial' ? 'Revisar: algún sensor no envía' : 'No envía';
  }

  ago(date: string | null): string {
    if (!date) return 'sin datos';
    const minutes = Math.max(0, Math.round((Date.now() - new Date(date).getTime()) / 60000));
    if (minutes < 60) return `hace ${minutes} min`;
    const hours = Math.round(minutes / 60);
    if (hours < 48) return `hace ${hours} h`;
    return `hace ${Math.round(hours / 24)} días`;
  }

  // ─── Mapa (fuera de la deteccion de cambios de Angular) ────────────────────

  private renderMap(): void {
    const element = this.mapCanvas?.nativeElement;
    if (!element || !this.data || this.map) return;

    this.zone.runOutsideAngular(() => {
      // Zoom abajo a la derecha (como Google Maps); arriba a la izquierda va la leyenda "Entradas"
      const map = L.map(element, { zoomControl: false, scrollWheelZoom: false, attributionControl: false });
      L.control.zoom({ position: 'bottomright' }).addTo(map);
      // Abajo, cada control nuevo se pone encima del anterior: el ojo queda sobre el + / −
      this.addLabelsControl(map);
      L.tileLayer(this.arcgis.getSatelliteTileUrl(), { maxZoom: 19 }).addTo(map);
      this.map = map;

      // Cada capa con su polígono: la leyenda oculta las áreas una a una (las de una regla) o todas a la vez
      const draw = (polygon: MapPolygon, isSubarea: boolean) => {
        const layer = this.drawPolygon(polygon, isSubarea);
        this.polygonLayerRecords.push({ polygon, layer });
        return layer;
      };
      // El del área y otros Polígonos suyos (contornos con otra propiedad Polígono), continuos; subáreas y polígonos
      // de otras áreas añadidos a mano, discontinuos (no son el área del dashboard). Se dibujan de la más grande a la
      // más pequeña: la pequeña queda encima y es la que se resalta y se pincha (Sector A1 dentro de Parcela A).
      const items = [
        ...(this.data!.polygon ? [{ polygon: this.data!.polygon, isSubarea: false, own: true }] : []),
        ...(this.data!.otherOwnPolygons ?? []).map(polygon => ({ polygon, isSubarea: false, own: false })),
        ...[...this.data!.subareaPolygons, ...(this.data!.manualPolygons ?? [])]
          .map(polygon => ({ polygon, isSubarea: true, own: false })),
      ].sort((a, b) => this.polygonSize(b.polygon) - this.polygonSize(a.polygon));
      let ownPolygon: L.Polygon | null = null;
      const subPolygons: L.Polygon[] = [];
      for (const item of items) {
        const layer = draw(item.polygon, item.isSubarea);
        if (item.own) ownPolygon = layer; else subPolygons.push(layer);
      }
      const rings = [ownPolygon, ...subPolygons].filter((p): p is L.Polygon => !!p);
      this.polygonLayers = rings;

      // "Fuera del área" solo con el contorno del área del dashboard: sin él (polígono sin dibujar) no se sabe
      // qué queda fuera, y los de las subáreas no bastan (un dispositivo de la parcela no está en su sector).
      const inside: MapDevice[] = [];
      const outside: MapDevice[] = [];
      for (const device of this.data!.devices) {
        const point = L.latLng(device.lat, device.long);
        (ownPolygon && !rings.some(ring => this.contains(ring, point)) ? outside : inside).push(device);
      }
      inside.forEach(device => this.drawDevice(map, device));

      this.fitView(map, ownPolygon, subPolygons, inside);
      map.on('zoomend', () => this.applyLabelVisibility());
      map.on('click', () => this.zone.run(() => this.closeDetail()));
      element.addEventListener('wheel', event => this.onWheel(event), { passive: false });
      // En captura: la cabecera de una cajita la arrastra a ella, no al mapa
      element.addEventListener('pointerdown', event => this.onLabelPointerDown(event), true);

      this.resizeObserver = new ResizeObserver(() => {
        map.invalidateSize();
        this.separateLabels();
      });
      this.resizeObserver.observe(element);
      this.zone.run(() => (this.outsideDevices = outside));
      this.applyLabelVisibility();
    });
  }

  private drawPolygon(polygon: MapPolygon, isSubarea: boolean): L.Polygon {
    const latLngs = polygon.coordinates.map(ring => ring.map(([lng, lat]) => L.latLng(lat, lng)));
    // El color de su entrada (regla de polígono, el del 🎨); sin él, el azul de siempre. Subáreas discontinuas
    const color = this.polygonColor(polygon);
    const style: L.PathOptions = isSubarea
      ? { color, weight: 1.5, dashArray: '6 5', fillColor: color, fillOpacity: 0.04 }
      : { color, weight: 3, fillColor: color, fillOpacity: 0.08 };
    const layer = L.polygon(latLngs, style).addTo(this.map!);
    this.bindPolygonInteractions(layer, polygon, style);
    return layer;
  }

  /**
   * Al pasar el ratón, el área se resalta y sale una etiqueta corta (su nombre). Al pincharla se abre un panel fijo
   * (no se va al mover el ratón) con su información y el botón para abrir su dashboard (no en el área de este
   * dashboard: ya se está en él). Se cierra con su ✕ o pinchando fuera. En móvil, igual al tocarla.
   */
  private bindPolygonInteractions(layer: L.Polygon, polygon: MapPolygon, style: L.PathOptions): void {
    const canOpen = this.canOpenArea(polygon);
    layer.bindTooltip(this.polygonTooltipHtml(polygon), {
      sticky: true, direction: 'top', offset: L.point(0, -8), opacity: 1, className: 'wm-area-tooltip',
    });
    layer.bindPopup(() => this.polygonPanel(polygon, canOpen), {
      className: 'wm-area-popup', closeButton: true, autoPanPadding: L.point(16, 16), maxWidth: 280,
    });
    layer.on('mouseover', () => layer.setStyle({ weight: (style.weight ?? 2) + 1.5, fillOpacity: 0.3 }));
    layer.on('mouseout', () => layer.setStyle(style));
    // Con el panel abierto no hace falta la etiqueta: lo taparía
    layer.on('popupopen', () => layer.closeTooltip());
    layer.on('click', event => L.DomEvent.stopPropagation(event));
  }

  /** Se puede abrir el dashboard de esa área: en la intranet, si lo tiene y no es el de este dashboard. */
  private canOpenArea(polygon: MapPolygon): boolean {
    return this.canOpenDevice && this.hasAreaDashboard(polygon) && !this.isOwnArea(polygon);
  }

  private hasAreaDashboard(polygon: MapPolygon): boolean {
    return polygon.hasDashboard ?? !!polygon.dashboardId;
  }

  private isOwnArea(polygon: MapPolygon): boolean {
    return polygon.areaEntryId.toLowerCase() === (this.data?.ownAreaEntryId ?? '').toLowerCase();
  }

  private openAreaDashboard(polygon: MapPolygon): void {
    if (!this.canOpenArea(polygon) || this.openingAreaDashboard) return;
    const instanceId = this.data?.areaInstanceDocumentId;
    if (!instanceId) {
      if (polygon.dashboardId) this.router.navigate(['/intranet/areas/dashboard', polygon.dashboardId]);
      return;
    }
    this.openingAreaDashboard = true;
    const userId = this.storage.get('userId') ?? '';
    this.areaDashboardSubscription = this.dashboardService
      .getDashboardByUserAreaInstanceNode(userId, instanceId, polygon.areaEntryId)
      .subscribe({
        next: dashboard => {
          this.openingAreaDashboard = false;
          this.router.navigate(['/intranet/areas/dashboard', dashboard.id]);
        },
        error: () => {
          this.openingAreaDashboard = false;
          this.toast.errorToast('No se pudo abrir el dashboard del área. Inténtalo de nuevo.');
        },
      });
  }

  /** Etiqueta al pasar el ratón: solo el nombre (lo demás, en el panel al pinchar). */
  private polygonTooltipHtml(polygon: MapPolygon): string {
    return `<strong>${this.escapeHtml(polygon.name)}</strong><small>Pulsa para ver más</small>`;
  }

  /**
   * Panel al pinchar un área: su nombre (con su color), el polígono que la pinta, dentro de qué áreas está, sus
   * dispositivos y el botón para abrir su dashboard. El botón es un elemento con su evento (no un enlace en el HTML).
   */
  private polygonPanel(polygon: MapPolygon, canOpen: boolean): HTMLElement {
    const escape = (text: string) => this.escapeHtml(text);
    const count = polygon.deviceCount ?? 0;
    const isOwn = this.isOwnArea(polygon);
    const rows: Array<[string, string]> = [
      ['Superficie', this.polygonSurfaceLabel(polygon)],
      ['Nº de dispositivos', String(count)],
    ];
    const panel = document.createElement('div');
    panel.className = 'wm-area-panel';
    panel.innerHTML =
      `<div class="wm-area-panel__head">`
      + `<i class="wm-area-panel__swatch" style="border-color:${this.polygonColor(polygon)};background:${this.polygonColor(polygon)}22"></i>`
      + `<strong>${escape(polygon.name)}</strong></div>`
      + rows.map(([label, value]) =>
        `<div class="wm-area-panel__row"><span>${label}</span><span>${escape(value)}</span></div>`).join('')
      + (isOwn
        ? '<div class="wm-area-panel__note">Es el área de este dashboard</div>'
        : !this.hasAreaDashboard(polygon) ? '<div class="wm-area-panel__note">Esta área no tiene dashboard</div>' : '');
    if (canOpen) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'wm-area-panel__open';
      const icon = document.createElement('span');
      icon.className = 'material-icons';
      icon.setAttribute('aria-hidden', 'true');
      icon.textContent = 'open_in_new';
      const label = document.createElement('span');
      label.textContent = `Ir a ${polygon.name}`;
      button.append(icon, label);
      button.addEventListener('click', event => {
        event.stopPropagation();
        this.zone.run(() => this.openAreaDashboard(polygon));
      });
      panel.appendChild(button);
    }
    return panel;
  }

  private polygonSurfaceLabel(polygon: MapPolygon): string {
    try {
      // GeoJSON cierra cada anillo repitiendo el primer punto. Se conserva el dato original del mapa.
      const coordinates = polygon.coordinates.map(ring => {
        if (ring.length < 3) throw new Error('Anillo incompleto');
        const first = ring[0];
        const last = ring[ring.length - 1];
        return first[0] === last[0] && first[1] === last[1] ? ring : [...ring, first];
      });
      const hectares = area(turfPolygon(coordinates)) / 10000;
      if (!Number.isFinite(hectares) || hectares < 0) return 'No disponible';
      return hectares.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ha';
    } catch {
      return 'No disponible';
    }
  }

  private escapeHtml(text: string): string {
    return text.replace(/[&<>"']/g, char => `&#${char.charCodeAt(0)};`);
  }

  /** Tamaño aproximado del polígono (área del primer anillo en grados): para dibujar los grandes debajo. */
  private polygonSize(polygon: MapPolygon): number {
    const ring = polygon.coordinates[0] ?? [];
    let sum = 0;
    for (let i = 0; i < ring.length; i++) {
      const [x1, y1] = ring[i];
      const [x2, y2] = ring[(i + 1) % ring.length];
      sum += x1 * y2 - x2 * y1;
    }
    return Math.abs(sum) / 2;
  }

  polygonColor(polygon: MapPolygon): string {
    return polygon.color || DEFAULT_POLYGON_COLOR;
  }

  private drawDevice(map: L.Map, device: MapDevice): void {
    const marker = L.marker([device.lat, device.long], { icon: this.nodeIcon(device), keyboard: true, title: this.displayName(device) })
      .on('click', event => {
        L.DomEvent.stopPropagation(event);
        if (this.ignoreNextLabelClick) return;
        const target = (event as L.LeafletMouseEvent).originalEvent?.target as HTMLElement | null;
        // ✕ de la cajita: solo la cierra
        if (target?.closest('.wm-label__close')) {
          this.setLabelClosed(device, true);
          return;
        }
        // La chincheta abre su cajita si estaba cerrada; pulsar una cajita la pone delante
        if (!target?.closest('.wm-label')) this.setLabelClosed(device, false);
        marker.setZIndexOffset(++this.labelsTop * 1000);
        this.zone.run(() => this.select(device));
      });
    if (this.isDeviceShown(device)) marker.addTo(map);
    this.markers.set(device.id, { marker, device });
  }

  private setLabelClosed(device: MapDevice, closed: boolean): void {
    if (this.closedLabels.has(device.id) === closed) return;
    if (closed) this.closedLabels.add(device.id); else this.closedLabels.delete(device.id);
    this.markers.get(device.id)?.marker.setIcon(this.nodeIcon(device));
    this.paintMovedLabel(device.id);
    this.separateLabels();
  }

  private nodeIcon(device: MapDevice): L.DivIcon {
    const closed = this.closedLabels.has(device.id) ? ' widget-map-node--closed' : '';
    return L.divIcon({ className: `widget-map-node${closed}`, html: this.nodeHtml(device), iconSize: [0, 0] });
  }

  /** ✕ de la cajita (va en las dos versiones de la cajita, por sensores y por variables). */
  private closeLabelHtml(): string {
    return '<button type="button" class="wm-label__close" title="Cerrar" aria-label="Cerrar">×</button>';
  }

  /** Atributos de la cajita: su dispositivo y, si se ha movido, su desplazamiento. */
  private labelAttrs(device: MapDevice): string {
    const offset = this.labelOffsets.get(device.id) ?? { x: 0, y: 0 };
    return `data-id="${this.escape(device.id)}" style="--dx:${offset.x}px;--dy:${offset.y}px"`;
  }

  /** Clase de la cajita movida (sombra algo mas marcada: flota sobre el mapa). */
  private movedClass(device: MapDevice): string {
    return this.labelOffsets.has(device.id) ? ' wm-label--moved' : '';
  }

  /** Linea de la chincheta a la cajita movida. */
  private leaderHtml(device: MapDevice): string {
    const offset = this.labelOffsets.get(device.id);
    const [x2, y2] = offset ? this.leaderEnd(offset) : [0, 0];
    // Dos lineas: una blanca ancha debajo (halo) y una oscura encima, para que se vea en cualquier fondo,
    // y un punto donde toca la cajita
    const line = (css: string) => `<line class="${css}" x1="0" y1="0" x2="${x2}" y2="${y2}"/>`;
    // 1x1 y overflow visible: un svg de tamaño 0 no se pinta (aunque la linea salga fuera)
    return `<svg class="wm-leader"${offset ? '' : ' hidden'} width="1" height="1">`
      + `${line('wm-leader__halo')}${line('wm-leader__line')}`
      + `<circle class="wm-leader__end" cx="${x2}" cy="${y2}" r="3.5"/></svg>`;
  }

  /**
   * Punto de la cajita donde acaba la linea (respecto a la chincheta): el mas cercano a la chincheta, para que
   * la linea no pase por debajo de la cajita y quede tapada. Sin mover, la cajita va centrada sobre la chincheta.
   */
  private leaderEnd(offset: LabelOffset): [number, number] {
    const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);
    const bottom = offset.y - LABEL_ABOVE_PIN;
    return [
      clamp(0, offset.x - offset.width / 2, offset.x + offset.width / 2),
      clamp(0, bottom - offset.height, bottom),
    ];
  }

  private onLabelPointerDown(event: PointerEvent): void {
    const target = event.target as HTMLElement | null;
    const head = target?.closest('.wm-label__head');
    const label = head?.closest<HTMLElement>('.wm-label');
    if (!head || !label?.dataset['id'] || event.button !== 0 || this.labelDrag) return;
    event.stopPropagation();
    event.preventDefault();
    const id = label.dataset['id'];
    const from = this.labelOffsets.get(id) ?? { x: 0, y: 0 };
    this.labelDrag = {
      id, pointerId: event.pointerId, startX: event.clientX, startY: event.clientY, moved: false,
      from: { x: from.x, y: from.y, width: label.offsetWidth, height: label.offsetHeight },
    };
    this.markers.get(id)?.marker.setZIndexOffset(++this.labelsTop * 1000);
    this.map?.dragging.disable();
    window.addEventListener('pointermove', this.onLabelPointerMove);
    // Si el navegador cancela el gesto (tablet: otro dedo, el sistema...) no llega pointerup: tambien se termina
    window.addEventListener('pointerup', this.onLabelPointerUp);
    window.addEventListener('pointercancel', this.onLabelPointerUp);
  }

  private onLabelPointerMove = (event: PointerEvent): void => {
    const drag = this.labelDrag;
    if (!drag || event.pointerId !== drag.pointerId) return;
    const dx = event.clientX - drag.startX;
    const dy = event.clientY - drag.startY;
    // Unos pixeles de margen: un clic con el pulso tembloroso sigue siendo un clic
    if (!drag.moved && Math.hypot(dx, dy) < 4) return;
    drag.moved = true;
    this.manualLabels.add(drag.id);
    const { x, y } = this.keepLabelInMap(drag.id, drag.from.x + dx, drag.from.y + dy);
    this.labelOffsets.set(drag.id, { ...drag.from, x, y });
    this.paintMovedLabel(drag.id);
  };

  /** La cajita no sale del mapa: fuera ya no se podria coger ni cerrar. */
  private keepLabelInMap(id: string, x: number, y: number): { x: number; y: number } {
    const label = this.markers.get(id)?.marker.getElement()?.querySelector<HTMLElement>('.wm-label');
    const canvas = this.mapCanvas?.nativeElement;
    if (!label || !canvas) return { x, y };
    const box = label.getBoundingClientRect();
    const area = canvas.getBoundingClientRect();
    const now = this.labelOffsets.get(id) ?? { x: 0, y: 0 };
    return {
      x: now.x + Math.max(area.left - box.left, Math.min(area.right - box.right, x - now.x)),
      y: now.y + Math.max(area.top - box.top, Math.min(area.bottom - box.bottom, y - now.y)),
    };
  }

  private onLabelPointerUp = (event: PointerEvent): void => {
    // Solo el dedo que arrastra: otro apoyado en el mapa (tablet) no termina el arrastre
    if (event.pointerId !== this.labelDrag?.pointerId) return;
    window.removeEventListener('pointermove', this.onLabelPointerMove);
    window.removeEventListener('pointerup', this.onLabelPointerUp);
    window.removeEventListener('pointercancel', this.onLabelPointerUp);
    this.map?.dragging.enable();
    const moved = this.labelDrag?.moved;
    this.labelDrag = null;
    if (moved) {
      this.ignoreNextLabelClick = true;
      setTimeout(() => (this.ignoreNextLabelClick = false));
      this.separateLabels();
    }
  };

  /**
   * Separa las cajitas que se pisan, sin guardar nada: se calcula al pintar con lo que se ve en ese momento.
   * Primero se quedan en su sitio las que caben; las que chocan buscan el hueco libre mas cercano. Las movidas a
   * mano, las chinchetas y los paneles del mapa no se mueven: las demas los esquivan.
   */
  private separateLabels(): void {
    const map = this.map;
    const zoomOk = !!map && map.getZoom() >= LABELS_MIN_ZOOM;
    if (!map || !zoomOk || !this.labelsOn || this.labelDrag) return;

    const size = map.getSize();
    const boxes = this.labelBoxes(map);
    const placed = boxes.filter(box => box.fixed);
    const labels = boxes.filter(box => !box.fixed);
    const insideMap = (box: LabelBox) => box.left >= 0 && box.top >= 0
      && box.left + box.width <= size.x && box.top + box.height <= size.y;

    const blocked = labels.filter(box => {
      const fits = insideMap(box) && !this.overlapWith(box, placed);
      if (fits) placed.push(box);
      return !fits;
    });
    for (const box of blocked) {
      this.moveToNearestGap(box, placed, size);
      placed.push(box);
    }
    labels.forEach(box => this.placeSeparatedLabel(box));
  }

  /** Prueba posiciones en circulos cada vez mas grandes alrededor de su sitio. Sin hueco, donde menos se pise. */
  private moveToNearestGap(box: LabelBox, placed: LabelBox[], size: L.Point): void {
    let best = { left: box.left, top: box.top, overlap: Infinity };
    for (let distance = LABEL_SEARCH_STEP; distance <= Math.max(size.x, size.y); distance += LABEL_SEARCH_STEP) {
      for (let i = 0; i < LABEL_SEARCH_ANGLES; i++) {
        const angle = (2 * Math.PI * i) / LABEL_SEARCH_ANGLES;
        box.left = Math.max(0, Math.min(size.x - box.width, box.homeLeft + distance * Math.cos(angle)));
        box.top = Math.max(0, Math.min(size.y - box.height, box.homeTop + distance * Math.sin(angle)));
        const overlap = this.overlapWith(box, placed);
        if (!overlap) return;
        if (overlap < best.overlap) best = { left: box.left, top: box.top, overlap };
      }
    }
    box.left = best.left;
    box.top = best.top;
  }

  /** Superficie que una cajita pisa de las demas (dejando entre ellas LABEL_GAP). */
  private overlapWith(box: LabelBox, others: LabelBox[]): number {
    return others.reduce((total, other) => {
      const width = Math.min(box.left + box.width, other.left + other.width) - Math.max(box.left, other.left) + LABEL_GAP;
      const height = Math.min(box.top + box.height, other.top + other.height) - Math.max(box.top, other.top) + LABEL_GAP;
      return width > 0 && height > 0 ? total + width * height : total;
    }, 0);
  }

  /** Las cajitas que se ven ahora en el mapa. Las que no entran en la separacion vuelven a su sitio. */
  private labelBoxes(map: L.Map): LabelBox[] {
    const bounds = map.getBounds();
    const boxes: LabelBox[] = [];
    for (const [id, { marker }] of this.markers) {
      const label = marker.getElement()?.querySelector<HTMLElement>('.wm-label');
      const onMap = map.hasLayer(marker) && bounds.contains(marker.getLatLng());
      const pin = map.latLngToContainerPoint(marker.getLatLng());
      // Las chinchetas tambien cuentan: una cajita apartada no tiene que tapar ninguna (ni la suya)
      if (onMap) {
        const left = pin.x - LABEL_PIN_RADIUS;
        const top = pin.y - LABEL_PIN_RADIUS;
        const size = LABEL_PIN_RADIUS * 2;
        boxes.push({ id: `pin:${id}`, left, top, width: size, height: size, homeLeft: left, homeTop: top, fixed: true });
      }
      if (!label || !onMap || this.closedLabels.has(id) || !label.offsetHeight) {
        if (!this.manualLabels.has(id)) this.resetLabel(id);
        continue;
      }
      const width = label.offsetWidth;
      const height = label.offsetHeight;
      const homeLeft = pin.x - width / 2;
      const homeTop = pin.y - LABEL_ABOVE_PIN - height;
      const fixed = this.manualLabels.has(id);
      const offset = fixed ? this.labelOffsets.get(id) : undefined;
      boxes.push({
        id, width, height, homeLeft, homeTop, fixed,
        left: homeLeft + (offset?.x ?? 0),
        top: homeTop + (offset?.y ?? 0),
      });
    }
    return [...boxes, ...this.mapPanelBoxes()];
  }

  /** Lo que flota sobre el mapa (leyenda, aviso, bandeja, botones): una cajita debajo quedaria tapada. */
  private mapPanelBoxes(): LabelBox[] {
    const canvas = this.mapCanvas?.nativeElement.getBoundingClientRect();
    if (!canvas) return [];
    return Array.from(this.host.nativeElement.querySelectorAll<HTMLElement>(MAP_PANELS)).map((panel, index) => {
      const { left, top, width, height } = panel.getBoundingClientRect();
      const inMap = { left: left - canvas.left, top: top - canvas.top };
      return { id: `panel:${index}`, ...inMap, width, height, homeLeft: inMap.left, homeTop: inMap.top, fixed: true };
    });
  }

  /** Al plegar la leyenda o cerrar el aviso cambia el sitio libre (despues de que Angular lo pinte). */
  private refreshLabelsAfterPanelChange(): void {
    setTimeout(() => this.separateLabels());
  }

  toggleLegend(): void {
    this.legendOpen = !this.legendOpen;
    this.refreshLabelsAfterPanelChange();
  }

  closePolygonNotice(): void {
    this.polygonNoticeClosed = true;
    this.refreshLabelsAfterPanelChange();
  }

  private placeSeparatedLabel(box: LabelBox): void {
    const x = Math.round(box.left - box.homeLeft);
    const y = Math.round(box.top - box.homeTop);
    if (!x && !y) {
      this.resetLabel(box.id);
      return;
    }
    this.labelOffsets.set(box.id, { x, y, width: box.width, height: box.height });
    this.paintMovedLabel(box.id);
  }

  /** Quita el desplazamiento automatico: rehace el icono, que sin desplazamiento no lleva linea. */
  private resetLabel(id: string): void {
    if (!this.labelOffsets.delete(id)) return;
    const item = this.markers.get(id);
    item?.marker.setIcon(this.nodeIcon(item.device));
  }

  /**
   * Coloca una cajita movida y su linea sobre el HTML que hay ahora en el mapa (sin volver a crear el icono).
   * Se usa al arrastrar y cada vez que se vuelve a pintar (la leyenda o la ✕ rehacen el HTML): el elemento se
   * busca cada vez (el de antes puede ya no estar) y la cajita se mide en ese momento (puede tener otras filas).
   */
  private paintMovedLabel(id: string): void {
    const offset = this.labelOffsets.get(id);
    const node = this.markers.get(id)?.marker.getElement();
    const label = node?.querySelector<HTMLElement>('.wm-label');
    if (!offset || !node || !label) return;
    // Cerrada no se ve (mide 0): se guarda el tamaño que tenia
    if (label.offsetHeight) {
      offset.width = label.offsetWidth;
      offset.height = label.offsetHeight;
    }
    label.style.setProperty('--dx', `${offset.x}px`);
    label.style.setProperty('--dy', `${offset.y}px`);
    label.classList.add('wm-label--moved');
    const leader = node.querySelector<SVGElement>('.wm-leader');
    const [x2, y2] = this.leaderEnd(offset);
    leader?.removeAttribute('hidden');
    leader?.querySelectorAll('line').forEach(line => {
      line.setAttribute('x2', String(x2));
      line.setAttribute('y2', String(y2));
    });
    leader?.querySelector('circle')?.setAttribute('cx', String(x2));
    leader?.querySelector('circle')?.setAttribute('cy', String(y2));
  }

  /** Tras marcar/desmarcar una variable: cada punto con sus valores visibles, o fuera si no le queda ninguno. */
  private refreshMarkers(): void {
    const map = this.map;
    if (!map) return;
    this.zone.runOutsideAngular(() => {
      for (const { marker, device } of this.markers.values()) {
        if (!this.isDeviceShown(device)) {
          marker.remove();
          continue;
        }
        marker.setIcon(this.nodeIcon(device));
        if (!map.hasLayer(marker)) marker.addTo(map);
        // La cajita puede haber cambiado de tamaño (variables ocultas): su linea se vuelve a calcular
        this.paintMovedLabel(device.id);
      }
      this.separateLabels();
    });
    if (this.selected && !this.isDeviceShown(this.selected)) this.closeDetail();
  }

  /** Encuadre: el poligono del area; si no hay, las subareas y los dispositivos; si no, la vista por defecto. */
  private fitView(map: L.Map, polygon: L.Polygon | null, subPolygons: L.Polygon[], devices: MapDevice[]): void {
    if (polygon) {
      map.fitBounds(polygon.getBounds(), { padding: [24, 24] });
      return;
    }
    // Sin el poligono del area: lo que haya, los contornos de las subareas y los dispositivos (sin "Fuera
    // del área" no se esconde ninguno; si alguno esta lejos, el aviso pide dibujar el poligono)
    const bounds = L.latLngBounds(devices.map(d => L.latLng(d.lat, d.long)));
    subPolygons.forEach(sub => bounds.extend(sub.getBounds()));
    if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 17 });
    } else {
      const view = this.arcgis.getDefaultMapView();
      map.setView([view.lat, view.lng], view.zoom);
    }
  }

  /** La rueda no hace zoom (deja hacer scroll al dashboard); Ctrl + rueda si. */
  private onWheel(event: WheelEvent): void {
    if (!event.ctrlKey || !this.map) return;
    event.preventDefault();
    if (event.deltaY < 0) this.map.zoomIn(); else this.map.zoomOut();
  }

  private applyLabelVisibility(): void {
    const zoomOk = !this.map || this.map.getZoom() >= LABELS_MIN_ZOOM;
    this.mapCanvas?.nativeElement.classList.toggle('widget-map__canvas--no-labels', !(this.labelsOn && zoomOk));
    this.separateLabels();
  }

  private contains(polygon: L.Polygon, point: L.LatLng): boolean {
    const rings = polygon.getLatLngs() as L.LatLng[][];
    const ring = rings[0] ?? [];
    let inside = false;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
      const [xi, yi, xj, yj] = [ring[i].lng, ring[i].lat, ring[j].lng, ring[j].lat];
      if ((yi > point.lat) !== (yj > point.lat) && point.lng < ((xj - xi) * (point.lat - yi)) / (yj - yi) + xi) {
        inside = !inside;
      }
    }
    return inside;
  }

  // ─── HTML de cada nodo (L.divIcon) ─────────────────────────────────────────

  private nodeHtml(device: MapDevice): string {
    if (this.usesVariables(device)) return this.variablesNodeHtml(device);
    const sorted = [...device.sensors].sort((a, b) => this.rank(a.status) - this.rank(b.status) || a.position - b.position);
    const many = device.sensors.length > MAX_LABEL_ROWS;
    const shown = many ? sorted.slice(0, MAX_LABEL_ROWS - 1) : device.sensors;
    const rows = shown.map(sensor => `
      <div class="wm-row${sensor.status !== 'ok' ? ' wm-row--late' : ''}">
        <span class="wm-row__pos">${sensor.position}</span>
        <span class="wm-row__type">${this.escape(this.typeLabel(sensor.type))}</span>
        <span class="wm-row__value">${this.escape(this.mainValue(sensor))}</span>
        <span class="wm-dot" style="background:${this.sensorColor(sensor.status)}"></span>
      </div>`).join('');
    const more = many ? `<div class="wm-more">+${device.sensors.length - shown.length} más</div>` : '';
    const count = device.sensors.length;
    const badge = count > 1 ? `<span class="wm-badge">${count}</span>` : '';
    return `${this.leaderHtml(device)}
      <div class="wm-label${this.movedClass(device)}" ${this.labelAttrs(device)}>${this.closeLabelHtml()}
        <div class="wm-label__head">
          <span>${this.escape(this.displayName(device))}</span>
          <span>${count === 1 ? '1 sensor' : `${count} sensores`}</span>
        </div>${rows}${more}
      </div>
      <button type="button" class="wm-point" style="--c:${this.deviceColor(device.status)}"
        aria-label="${this.escape(this.displayName(device))}">${badge}</button>`;
  }

  /** Con variables: una fila por valor visible (punto del color de la variable), agrupadas en el mismo punto. */
  private variablesNodeHtml(device: MapDevice): string {
    const values = this.visibleValues(device);
    const many = values.length > MAX_LABEL_ROWS;
    const shown = many ? values.slice(0, MAX_LABEL_ROWS - 1) : values;
    // Si una variable sale en varios sensores del mismo dispositivo se indica la posicion
    const repeated = new Set(values.map(v => v.variableId).filter((id, i, all) => all.indexOf(id) !== i));
    const rows = shown.map(value => {
      const variable = this.variableOf(value);
      const label = variable ? this.variableLabel(variable) : '';
      const suffix = repeated.has(value.variableId) ? ` (${value.position})` : '';
      return `
      <div class="wm-row${value.status !== 'ok' ? ' wm-row--late' : ''}">
        <span class="wm-dot" style="background:${variable ? this.variableColor(variable) : '#92949c'}"></span>
        <span class="wm-row__type">${this.escape(label + suffix)}</span>
        <span class="wm-row__value">${this.escape(this.formatVariableValue(value))}</span>
      </div>`;
    }).join('');
    const more = many ? `<div class="wm-more">+${values.length - shown.length} más</div>` : '';
    return `${this.leaderHtml(device)}
      <div class="wm-label wm-label--variables${this.movedClass(device)}" ${this.labelAttrs(device)}>${this.closeLabelHtml()}
        <div class="wm-label__head"><span>${this.escape(this.displayName(device))}</span></div>${rows}${more}
      </div>
      <button type="button" class="wm-point" style="--c:${this.deviceColor(device.status)}"
        aria-label="${this.escape(this.displayName(device))}"></button>`;
  }

  private rank(status: MapSensorStatus): number {
    return status === 'ok' ? 2 : status === 'stale' ? 0 : 1;
  }

  // ─── Valores ───────────────────────────────────────────────────────────────

  private typeLabel(type: number): string {
    const label = this.catalog.get(type)?.label ?? `Tipo ${type}`;
    return label.split(' (')[0];
  }

  /** Variable principal del catalogo; si no llega en la lectura, la primera que haya. */
  private mainValue(sensor: MapSensor): string {
    if (sensor.status === 'never') return '—';
    const main = this.catalog.get(sensor.type)?.mainVariable;
    const key = main && main in sensor.values ? main : Object.keys(sensor.values)[0];
    return key ? this.formatValue(sensor.type, key, sensor.values[key]) : '—';
  }

  private formatValue(type: number, key: string, value: number | string): string {
    const unit = this.catalog.get(type)?.variables.find(variable => variable.id === key)?.unit ?? '';
    const text = typeof value === 'number'
      ? (Math.round(value * 10) / 10).toString().replace('.', ',')
      : value;
    return unit ? `${text} ${unit}` : text;
  }

  private escape(text: string): string {
    return text.replace(/[&<>"']/g, char => (
      { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char] as string
    ));
  }
}
